import { Injectable, PLATFORM_ID, Inject, signal } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { firstValueFrom, map } from 'rxjs';
import { isPlatformBrowser } from '@angular/common';
import { TenantConfigService } from '../config/tenant-config';
import { OrderPayload, CartResponse, CreateFedexLabelPayload, OrderShippingPayload } from '../models/order-payload.model';
import { PLATFORM_API_URL } from '../config/api-config';
import { environment } from '../../../environments/environment';
import { extractApiErrorMessage } from '../errors/error-message.util';

@Injectable({
    providedIn: 'root',
})
export class OrderProcessingService {
    cartItemCount = signal<number>(0);
    isUploading = signal<boolean>(false);
    showErrorModal = signal<boolean>(false);
    errorModalMessage = signal<string>('');

    constructor(
        private http: HttpClient,
        private tenantConfig: TenantConfigService,
        @Inject(PLATFORM_ID) private platformId: Object
    ) {
        if (isPlatformBrowser(this.platformId)) {
            this.updateCartCountFromStorage();
        }
    }

    isCartAlreadyPlacedError(error: any): boolean {
        if (!error) return false;
        const status = error?.status ?? error?.statusCode ?? error?.error?.status ?? error?.error?.statusCode;
        const body = error?.error || error;
        const errorCode = body?.error_code ?? body?.data?.error_code ?? error?.error_code ?? error?.data?.error_code;
        return (status === 400 && errorCode === 'CART_ALREADY_PLACED') || errorCode === 'CART_ALREADY_PLACED';
    }

    handleCartAlreadyPlacedError(error: any): boolean {
        if (this.isCartAlreadyPlacedError(error)) {
            if (isPlatformBrowser(this.platformId)) {
                localStorage.clear();
                this.cartItemCount.set(0);
                document.body.style.overflow = 'hidden';
            }
            const msg = extractApiErrorMessage(error, 'This cart has already been placed. Please start a new order.');
            this.errorModalMessage.set(msg);
            this.showErrorModal.set(true);
            return true;
        }
        return false;
    }

    startNewOrder(): void {
        if (isPlatformBrowser(this.platformId)) {
            document.body.style.overflow = '';
            localStorage.clear();
            this.cartItemCount.set(0);
            this.showErrorModal.set(false);
            window.location.href = '/';
        }
    }

    private updateCartCountFromStorage() {
        try {
            const savedCart = localStorage.getItem('cart_response');
            if (savedCart) {
                const parsed: CartResponse = JSON.parse(savedCart);
                const count = parsed?.data?.cartItems?.length || 0;
                this.cartItemCount.set(count);
            } else {
                this.cartItemCount.set(0);
            }
        } catch (e) {
            console.error('Failed to parse cart_response from local storage', e);
        }
    }

    async addToCart(payload: OrderPayload): Promise<any> {
        if (isPlatformBrowser(this.platformId)) {
            const websiteId = this.tenantConfig.settings?.['websiteId'];
            if (!websiteId) {
                console.error('Website ID not found in tenant settings.');
                return null;
            }

            const headers = new HttpHeaders({
                'X-Tenant': websiteId.toString(),
            });

            // If the cart has existing items, retrieve the requestNumber from local storage to include it in the new payload
            try {
                const savedCart = localStorage.getItem('cart_response');
                if (savedCart) {
                    const parsed: CartResponse = JSON.parse(savedCart);
                    const cartItems = parsed?.data?.cartItems || [];
                    const cart_id = parsed?.data?.id;
                    if (cartItems.length > 0 && cart_id) {
                        payload.cart_id = cart_id;
                    }
                }
            } catch (e) {
                console.error('Failed to read requestNumber from local storage cart_response', e);
            }

            try {
                const response: any = await firstValueFrom(
                    this.http.post(`${PLATFORM_API_URL}/add-to-cart`, payload, { headers })
                );

                if (response && response.success === false) {
                    throw response;
                }

                // Save to local storage with lastActive
                const cartToStore = response && typeof response === 'object'
                    ? { ...response, lastActive: Date.now() }
                    : response;
                payload.order_type == "Personal" ? null : localStorage.setItem('cart_response', JSON.stringify(cartToStore));
                localStorage.removeItem("shipping_response")
                localStorage.removeItem("full_order")
                localStorage.removeItem("payment_response")
                this.updateCartCountFromStorage();

                return response;
            } catch (error) {
                console.error('Error adding to cart:', error);
                this.handleCartAlreadyPlacedError(error);
                throw error;
            }
        }
        return null;
    }

    async uploadFile(file: File): Promise<any> {
        if (isPlatformBrowser(this.platformId)) {
            const websiteId = this.tenantConfig.settings?.['websiteId'];
            if (!websiteId) {
                console.error('Website ID not found in tenant settings.');
                throw new Error('Website ID not found');
            }

            const headers = new HttpHeaders({
                'X-Tenant': websiteId.toString(),
            });

            const formData = new FormData();
            formData.append('file', file);

            try {
                const response: any = await firstValueFrom(
                    this.http.post(`${PLATFORM_API_URL}/upload-user-doc-files`, formData, { headers })
                );
                if (response && response.success === false) {
                    throw response;
                }
                return response;
            } catch (error) {
                console.error('Error uploading file:', error);
                this.handleCartAlreadyPlacedError(error);
                throw error;
            }
        }
        return null;
    }

    async uploadShippingFile(file: File): Promise<any> {
        if (isPlatformBrowser(this.platformId)) {
            const websiteId = this.tenantConfig.settings?.['websiteId'];
            if (!websiteId) {
                console.error('Website ID not found in tenant settings.');
                throw new Error('Website ID not found');
            }

            const headers = new HttpHeaders({
                'X-Tenant': websiteId.toString(),
            });

            const formData = new FormData();
            formData.append('file', file);

            try {
                const response: any = await firstValueFrom(
                    this.http.post(`${PLATFORM_API_URL}/upload-user-shipping-files`, formData, { headers })
                );
                if (response && response.success === false) {
                    throw response;
                }
                return response;
            } catch (error) {
                console.error('Error uploading shipping file:', error);
                this.handleCartAlreadyPlacedError(error);
                throw error;
            }
        }
        return null;
    }

    async submitOrderForm(payload: any): Promise<any> {
        if (isPlatformBrowser(this.platformId)) {
            const websiteId = this.tenantConfig.settings?.['websiteId'];
            if (!websiteId) {
                console.error('Website ID not found in tenant settings.');
                return null;
            }

            const headers = new HttpHeaders({
                'X-Tenant': websiteId.toString(),
            });

            try {
                const response: any = await firstValueFrom(
                    this.http.post(`${PLATFORM_API_URL}/order-form`, payload, { headers })
                );
                if (response && response.success === false) {
                    throw response;
                }
                return response;
            } catch (error) {
                console.error('Error submitting order form:', error);
                this.handleCartAlreadyPlacedError(error);
                throw error;
            }
        }
        return null;
    }

    async createShippingLabel(cartId: any, payload: CreateFedexLabelPayload, hideGlobalLoader: boolean = false): Promise<{ success: boolean, data?: any, error?: any }> {
        if (isPlatformBrowser(this.platformId)) {
            const websiteId = this.tenantConfig.settings?.['websiteId'];
            if (!websiteId) {
                console.error('Website ID not found in tenant settings.');
                return { success: false, error: 'Website ID not found' };
            }

            const headers = new HttpHeaders({
                'X-Tenant': websiteId.toString(),
            });

            if (!hideGlobalLoader) {
                this.isUploading.set(true);
            }
            try {
                const response: any = await firstValueFrom(
                    this.http.post<any>(`${PLATFORM_API_URL}/create-fedex-label/${cartId}`, payload, { headers })
                );
                if (!hideGlobalLoader) {
                    this.isUploading.set(false);
                }
                if (response && response.success === false) {
                    this.handleCartAlreadyPlacedError(response);
                    return { success: false, error: response };
                }
                return { success: true, data: response };
            } catch (error) {
                if (!hideGlobalLoader) {
                    this.isUploading.set(false);
                }
                console.error('Error creating shipping label:', error);
                this.handleCartAlreadyPlacedError(error);
                return { success: false, error };
            }
        }
        return { success: false, error: 'Not in browser' };
    }

    async saveOrderShipping(cartId: any, payload: OrderShippingPayload): Promise<{ success: boolean, data?: any, error?: any }> {
        if (isPlatformBrowser(this.platformId)) {
            const websiteId = this.tenantConfig.settings?.['websiteId'];
            if (!websiteId) {
                console.error('Website ID not found in tenant settings.');
                return { success: false, error: 'Website ID not found' };
            }

            const headers = new HttpHeaders({
                'X-Tenant': websiteId.toString(),
            });

            this.isUploading.set(true);
            try {
                const response: any = await firstValueFrom(
                    this.http.post<any>(`${PLATFORM_API_URL}/order-shipping/${cartId}`, payload, { headers })
                );
                this.isUploading.set(false);
                if (response && response.success === false) {
                    this.handleCartAlreadyPlacedError(response);
                    return { success: false, error: response };
                }
                return { success: true, data: response };
            } catch (error) {
                this.isUploading.set(false);
                console.error('Error saving order shipping:', error);
                this.handleCartAlreadyPlacedError(error);
                return { success: false, error };
            }
        }
        return { success: false, error: 'Not in browser' };
    }

    async deleteItemFromCart(cartItemId: number): Promise<any> {
        if (isPlatformBrowser(this.platformId)) {
            const websiteId = this.tenantConfig.settings?.['websiteId'];
            if (!websiteId) {
                console.error('Website ID not found in tenant settings.');
                return null;
            }

            const headers = new HttpHeaders({
                'X-Tenant': websiteId.toString(),
            });

            try {
                const response = await firstValueFrom(
                    this.http.delete<any>(`${PLATFORM_API_URL}/delete-item-from-cart/${cartItemId}`, { headers })
                );

                if (response && response.success === false) {
                    throw response;
                }

                if (response) {
                    // Update localStorage with the response and lastActive
                    const cartToStore = response.data
                        ? { ...response, lastActive: Date.now() }
                        : { data: response, lastActive: Date.now() };
                    localStorage.setItem('cart_response', JSON.stringify(cartToStore));
                    this.updateCartCountFromStorage();
                }

                return response;
            } catch (error) {
                console.error('Error deleting item from cart:', error);
                this.handleCartAlreadyPlacedError(error);
                throw error;
            }
        }
        return null;
    }

    async destroyCart(requestNumber: string): Promise<any> {
        if (isPlatformBrowser(this.platformId)) {
            const websiteId = this.tenantConfig.settings?.['websiteId'];
            if (!websiteId) {
                console.error('Website ID not found in tenant settings.');
                return null;
            }

            const headers = new HttpHeaders({
                'X-Tenant': websiteId.toString(),
            });

            try {
                const response = await firstValueFrom(
                    this.http.delete<any>(`${PLATFORM_API_URL}/destroy-cart/${requestNumber}`, { headers })
                );

                if (response && response.success === false) {
                    throw response;
                }

                // Clear storage and update count
                localStorage.removeItem('cart_response');
                this.updateCartCountFromStorage();

                return response;
            } catch (error) {
                console.error('Error destroying cart:', error);
                this.handleCartAlreadyPlacedError(error);
                throw error;
            }
        }
        return null;
    }

    async submitCartInvoicePayment(cartId: any, payload: {
        paymentToken: string;
        last4: string;
        customerName: string;
        email: string;
        zipCode: string;
    }): Promise<any> {
        if (isPlatformBrowser(this.platformId)) {
            const websiteId = this.tenantConfig.settings?.['websiteId'];
            if (!websiteId) {
                console.error('Website ID not found in tenant settings.');
                return null;
            }

            const headers = new HttpHeaders({
                'X-Tenant': websiteId.toString(),
            });

            try {
                const response: any = await firstValueFrom(
                    this.http.post<any>(`${PLATFORM_API_URL}/cart-invoice-payment/${cartId}`, payload, { headers })
                );
                if (response && response.success === false) {
                    throw response;
                }
                return response;
            } catch (error) {
                console.error('Error submitting cart invoice payment:', error);
                this.handleCartAlreadyPlacedError(error);
                throw error;
            }
        }
        return null;
    }

    async getAvailableCountries(): Promise<any[]> {
        try {
            const res = await firstValueFrom(
                this.http.get<any[]>(`${environment.fedexApiUrl}/api/fedex/available-countries`).pipe(
                    map((countries: any) => countries.filter((c: any) => c.CountryCode !== 'US' && c.Name !== "United States"))
                )
            );
            return res || [];
        } catch (error) {
            console.error('Failed to load countries', error);
            this.handleCartAlreadyPlacedError(error);
            return [];
        }
    }

    async getCanadaStates(): Promise<any[]> {
        try {
            const res = await firstValueFrom(
                this.http.get<any[]>(`${environment.fedexApiUrl}/api/canada-states`)
            );
            return res || [];
        } catch (error) {
            console.error('Failed to load Canada states', error);
            this.handleCartAlreadyPlacedError(error);
            return [];
        }
    }
    async getFedexRate(cartId: any): Promise<{ "2days": number; priority: number; international: number } | any> {
        if (isPlatformBrowser(this.platformId)) {
            const websiteId = this.tenantConfig.settings?.['websiteId'];
            let headers = new HttpHeaders();
            if (websiteId) {
                headers = headers.set('X-Tenant', websiteId.toString());
            }

            try {
                const response = await firstValueFrom(
                    this.http.get<any>(`${PLATFORM_API_URL}/get-fedex-rate/${cartId}`, { headers })
                );
                return response;
            } catch (error) {
                console.error('Error getting FedEx rate:', error);
                this.handleCartAlreadyPlacedError(error);
                return null;
            }
        }
        return null;
    }


    async getPaymentOptions(cartId: any): Promise<any> {
        if (isPlatformBrowser(this.platformId)) {
            const websiteId = this.tenantConfig.settings?.['websiteId'];
            if (!websiteId) {
                console.error('Website ID not found in tenant settings.');
                return null;
            }

            const headers = new HttpHeaders({
                'X-Tenant': websiteId.toString(),
            });

            try {
                const response = await firstValueFrom(
                    this.http.get<any>(`${PLATFORM_API_URL}/get-payment-options/${cartId}`, { headers })
                );
                return response;
            } catch (error) {
                console.error('Error getting payment options:', error);
                this.handleCartAlreadyPlacedError(error);
                throw error;
            }
        }
        return null;
    }

    async submitPaymentOption(cartId: any, payload: { paymentOption: string }): Promise<any> {
        if (isPlatformBrowser(this.platformId)) {
            const websiteId = this.tenantConfig.settings?.['websiteId'];
            if (!websiteId) {
                console.error('Website ID not found in tenant settings.');
                return null;
            }

            const headers = new HttpHeaders({
                'X-Tenant': websiteId.toString(),
            });

            this.isUploading.set(true);
            try {
                const response: any = await firstValueFrom(
                    this.http.post<any>(`${PLATFORM_API_URL}/pay-cart-invoice-later/${cartId}`, payload, { headers })
                );
                this.isUploading.set(false);
                if (response && response.success === false) {
                    throw response;
                }
                return response;
            } catch (error) {
                this.isUploading.set(false);
                console.error('Error submitting payment option:', error);
                this.handleCartAlreadyPlacedError(error);
                throw error;
            }
        }
        return null;
    }
}
